import {useCallback, useMemo} from 'react';
import moment, {Moment} from 'moment';
import {Calendar} from '../../../calendar/calendar';
import {DateRangeType} from '../../../date-range-input/date-range-utils';
import {
  OnCareServiceCreatingItem,
  OnCareServiceEditItem,
  OnCareServiceClickItem,
  CareServiceItem,
} from '../data/care-service-item';
import {
  createNewCareServiceItem,
  transformCareServicesItems2CalendarData,
} from '../data/care-service-data-transformation';
import {CareServicesData} from '../data/use-care-services-data';
import {UserAvatar} from '../../../../screens/private/office/professional-caregiver/planner/calendar/shared/user-avatar';
import {DateSpanApi, EventContentArg} from '@fullcalendar/core';

export type CareServicesCalendarProps = {
  data: CareServicesData;
  range: DateRangeType;
  date: Moment;
  canWrite?: boolean;
  onClickItem: OnCareServiceClickItem;
  onCreatingItem: OnCareServiceCreatingItem;
  onEditItem: OnCareServiceEditItem;
};

export function CareServicesCalendar(props: CareServicesCalendarProps) {
  const calendarData = useMemo(() => {
    // if (props.data.items.length !== 0) {
    //   console.log('Calendar data recalculated', props.data.items);
    // }

    return transformCareServicesItems2CalendarData(props.data.items);
  }, [props.data.items]);

  const canSelect = props.canWrite && !props.data.updating;

  const selectAllow = useCallback(
    (selectionInfo: DateSpanApi) => {
      if (!canSelect) return false;

      const start = selectionInfo.start;
      const end = selectionInfo.end;
      const durationMs = end.getTime() - start.getTime();
      const maxDurationMs = 24 * 60 * 60 * 1000; // 24 hours in ms

      return durationMs > 0 && durationMs <= maxDurationMs;
    },
    [canSelect]
  );

  return (
    <Calendar
      className="care-services-calendar"
      data={calendarData}
      dataLoading={props.data.fetching}
      dataUpdating={props.data.updating}
      range={props.range}
      date={props.date}
      allDaySlot={false}
      editable={canSelect}
      selectable={canSelect}
      selectAllow={selectAllow}
      onClickItem={(evt, calendarEvt) => {
        const item = props.data.items.find((it) => it.id === calendarEvt.id);
        props.onClickItem(item);
      }}
      onCreateSelection={(selectionInfo) => {
        const start = moment(selectionInfo.start);
        const end = moment(selectionInfo.end);
        const item = createNewCareServiceItem({start, end});
        props.onCreatingItem(item);
      }}
      onEditSelection={(changeInfo) => {
        const item = props.data.items.find((it) => it.id === changeInfo.event.id);
        if (!item) return props.onEditItem(undefined);

        const start = moment(changeInfo.event.start);
        const end = moment(changeInfo.event.end);

        const newItem: CareServiceItem = {
          ...item,
          startDate: start,
          endDate: end,
        };

        props.onEditItem(newItem);
      }}
      eventContent={
        props.range === '1d' || props.range === '1w'
          ? (eventInfo: EventContentArg) => {
              if (props.data.fetching || props.data.items.length === 0) return null;

              const item = eventInfo.event.extendedProps['item'] as CareServiceItem;
              const usersTypeList =
                props.data.mode === 'senior' ? props.data.caregivers : props.data.seniors;
              const userAvatars = usersTypeList.items?.filter(
                (c) =>
                  item.responsibleUserIds?.includes(c.user.id!) || c.user.id === item.targetUserId
              );

              return (
                <div className="fc-event-main-frame">
                  {/* --- Time of the Shift --- */}
                  <div className="fc-event-time">{eventInfo.timeText}</div>
                  <div className="fc-event-title-container">
                    {/* --- Avatars of Caregivers --- */}
                    {userAvatars?.length ? (
                      <div className="fc-event-avatars">
                        {userAvatars.map((ua) => (
                          <div key={ua.user.id} className="fc-event-avatar">
                            <UserAvatar
                              key={ua.user.id}
                              name={ua.user.displayName || ua.user.name}
                              size="s"
                              sx={{width: 16, height: 16}}
                              picture={ua.user.picture}
                            />
                            <div className="fc-event-title">
                              {ua.user.displayName || ua.user.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : undefined}
                    {/* --- Title of the Shift --- */}
                    <div className="fc-event-title">{eventInfo.event.title}</div>
                  </div>
                </div>
              );
            }
          : undefined
      }
    />
  );
}
